import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { Divider, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { useMutation } from '@tanstack/react-query'
import NotificationAPI from 'api/faber/NotificationAPI'
import OrbitSession from 'hooks/useCookies'

export default function ResendReceiptDialog({
  receiptDialogOpen,
  handleDialogClose,
  transanctionNo,
  email,
  userId,
  transactionType,
  isExternal,
  paymentIntent
}: ReceiptDialog) {
  const faberToken = OrbitSession.get('faberToken')
  const [recipientEmail, setRecipientEmail] = useState('')

  const {
    mutate: sendSubscriptionReceipt,
    isLoading: isReceiptSendingLoading
  } = useMutation(NotificationAPI.sendSubscriptionReceipt, {
    onSettled: () => {
      handleDialogClose()
    },
    onSuccess: () => {
      toast.success('Receipt successfully sent')
    },
    onError: () => {
      toast.error('There is an error sending receipt to customer')
    }
  })

  const {
    mutate: sendOtpackReceipt,
    isLoading: isOtpackReceiptSendingLoading
  } = useMutation(NotificationAPI.sendOtpackReceipt, {
    onSettled: () => {
      handleDialogClose()
    },
    onSuccess: () => {
      toast.success('Receipt successfully sent')
    },
    onError: () => {
      toast.error('There is an error sending receipt to customer')
    }
  })

  const {
    mutate: sendGlobalReceipt,
    isLoading: isGlobalReceiptSendingLoading
  } = useMutation(
    isExternal
      ? NotificationAPI.sendExternalGlobalReceipt
      : NotificationAPI.sendGlobalReceipt,
    {
      onSettled: () => {
        handleDialogClose()
      },
      onSuccess: () => {
        toast.success('Receipt successfully sent')
      },
      onError: () => {
        toast.error('There is an error sending receipt to customer')
      }
    }
  )

  const resendReceipt = () => {
    if (userId?.includes('|')) {
      sendGlobalReceipt({
        paymentId: isExternal ? transanctionNo : paymentIntent,
        userId: userId,
        faberToken: faberToken,
        ...(recipientEmail && { email: recipientEmail })
      })
    } else if (transactionType?.includes('ONETIMEPACK')) {
      sendOtpackReceipt({
        paymentId: transanctionNo,
        faberToken: faberToken
      })
    } else {
      sendSubscriptionReceipt({
        paymentId: transanctionNo,
        faberToken: faberToken
      })
    }
  }

  return (
    <Dialog
      open={receiptDialogOpen}
      onClose={handleDialogClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='alert-dialog-title' sx={{ fontSize: '1.2em' }}>
        Send Receipt
      </DialogTitle>
      <Divider />
      <DialogContent>
        <DialogContentText id='alert-dialog-description'>
          By taking this step, the customer will receive the receipt for the
          specific transaction: <b>{transanctionNo}</b>.
          <br />
          The receipt will be sent to the customer&apos;s default email:{' '}
          <b>{email}</b>.
          <br />
          <br />
          To send to a different email, please enter it below.
          <TextField
            autoFocus
            margin='dense'
            id='email'
            label='Recipient Email (Optional)'
            type='email'
            fullWidth
            variant='standard'
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose}>Cancel</Button>
        <LoadingButton
          variant='contained'
          onClick={resendReceipt}
          loading={
            isReceiptSendingLoading ||
            isOtpackReceiptSendingLoading ||
            isGlobalReceiptSendingLoading
          }
          autoFocus
        >
          Send Receipt
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

interface ReceiptDialog {
  receiptDialogOpen: boolean
  handleDialogClose: () => void
  transanctionNo: string
  email: string
  userId: string
  transactionType: string
  isExternal: boolean
  paymentIntent: string
}
